import { Add, Box, Button, Caption, Tooltip, TooltipContent, TooltipTrigger, Upload } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { t } from "i18next";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import style from "./reports-sub-menu.scss";
import { File } from "components/icons/File";
import { LockIcon } from "components/icons/LockIcon";
import { subpageContext } from "components/layout/ApplicationLayout";
import { useIsChildRouteEnabled } from "components/navigation/hooks/useIsChildRouteEnabled";
import { useIsRouteEnabled } from "components/navigation/hooks/useIsRouteEnabled";
import { NavigationSubMenuItem } from "components/navigation/navigation-panel/sub-navigation-panel/contents/navigation-sub-menu-item/NavigationSubMenuItem";
import { NavigationDivider } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-divider/NavigationDivider";
import { NavigationLabel } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-label/NavigationLabel";
import ImportReportViews from "components/reports/ImportReportView";
import LoadReportPathsModal from "components/reports/LoadReportPathsModal";
import { REPORTS_OUR_VIEWS_ROUTE, REPORTS_TEMPLATE_ROUTE, RouteDefinition } from "components/router/Routes";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";

import testIds from "testIds.json";

interface Props {
    mainRoute: RouteDefinition;
    enteredFromMainNavigation: boolean;
}

const lastScrollPosition = { topPixels: 0 };

export const ReportsSubmenu = (props: Props) => {
    const isRouteEnabled = useIsRouteEnabled();
    const { isChildRouteEnabled, isChildRouteAdvertised } = useIsChildRouteEnabled();

    const context = React.useContext(subpageContext);
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const isUserAllowedToCreateCustomViews = userSessionService.permittedToCreateCustomViews("reports", user);
    const isUserAllowedToViewCustomViews = userSessionService.permittedToViewCustomViews("reports", user);
    const [isReportUploadModalVisible, setIsReportUploadModalVisible] = React.useState(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.enteredFromMainNavigation) {
            lastScrollPosition.topPixels = 0;
        }
        scrollContainerRef.current?.scrollTo({ top: lastScrollPosition.topPixels });
    }, [props.enteredFromMainNavigation]);

    if (!props.mainRoute || !isRouteEnabled(props.mainRoute)) {
        return null;
    }

    return (
        <Box columnLayout className={style.container}>
            {props.mainRoute.childRoutes.map((childroute) => {
                if (!isChildRouteEnabled(childroute)) {
                    return null;
                }

                if (childroute === REPORTS_TEMPLATE_ROUTE) {
                    return (
                        <React.Fragment key={childroute.path}>
                            <NavigationDivider />
                            <NavigationLabel caption>{t("Settings.ssoConfigurations.configuration")}</NavigationLabel>
                            <NavigationSubMenuItem
                                key={childroute.path}
                                to={childroute.path}
                                isAdvertised={isChildRouteAdvertised(childroute)}
                                testId={childroute.testId}
                            >
                                {t(childroute.title)}
                            </NavigationSubMenuItem>
                            <NavigationDivider />
                        </React.Fragment>
                    );
                }

                if (childroute === REPORTS_OUR_VIEWS_ROUTE) {
                    if (!isUserAllowedToViewCustomViews) {
                        return null;
                    }

                    return (
                        <React.Fragment key={childroute.path}>
                            <NavigationLabel caption>{t("Routes.ourViews")}</NavigationLabel>

                            <Box
                                columnLayout
                                overflowY="Auto"
                                className={style.scrollContainer}
                                ref={scrollContainerRef}
                                onScroll={(e) => (lastScrollPosition.topPixels = e.currentTarget.scrollTop)}
                            >
                                {childroute.childRoutes.length > 0 ? (
                                    childroute.childRoutes.map((route) => {
                                        if (!isChildRouteEnabled(route)) {
                                            return null;
                                        }

                                        return (
                                            <NavigationSubMenuItem
                                                key={route.path}
                                                to={route.path}
                                                leftSection={
                                                    <File size={20} color={"var(--app-subNavigationPanel-iconColor)"} />
                                                }
                                                rightSection={
                                                    !route.isShared && (
                                                        <Tooltip placement="top">
                                                            <TooltipContent>
                                                                {t("CreateReportView.privateView.title")}
                                                            </TooltipContent>
                                                            <TooltipTrigger>
                                                                <LockIcon
                                                                    size={20}
                                                                    color={"var(--app-subNavigationPanel-iconColor)"}
                                                                />
                                                            </TooltipTrigger>
                                                        </Tooltip>
                                                    )
                                                }
                                                isAdvertised={isChildRouteAdvertised(route)}
                                                testId={testIds.navigation.reports.customViews.customViewLink}
                                            >
                                                {t(route.title)}
                                            </NavigationSubMenuItem>
                                        );
                                    })
                                ) : (
                                    <Box paddingLeft={"Sm"} paddingRight={"Sm"} paddingBottom={"Xxs"}>
                                        <Caption>{t("ErasureReportsTable.customViewsEmptyStateMessage")}</Caption>
                                    </Box>
                                )}
                            </Box>

                            {isUserAllowedToCreateCustomViews && (
                                <Box columnLayout className={style.footer}>
                                    <NavigationDivider />

                                    <Button
                                        variant={"Tertiary"}
                                        icon={<Add color={"--app-subNavigationPanel-iconColor"} />}
                                        onClick={() => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.REPORT_VIEW,
                                                action: Action.ADD_REPORT_VIEW,
                                            });
                                            context?.setSubpage({
                                                content: (
                                                    <LoadReportPathsModal setVisible={() => true} visible={true} />
                                                ),
                                                isModal: false,
                                            });
                                        }}
                                        className={classNames(style.navigationButton)}
                                        testId={testIds.navigation.reports.customViews.createReportViewButton}
                                    >
                                        {t("Common.createNew")}
                                    </Button>

                                    <Button
                                        variant={"Tertiary"}
                                        icon={<Upload color={"--app-subNavigationPanel-iconColor"} />}
                                        onClick={(event) => {
                                            usageStatisticsService.sendEvent({
                                                category: Category.REPORT_VIEW,
                                                action: Action.IMPORT_REPORT_VIEW,
                                            });
                                            setIsReportUploadModalVisible(true);
                                            event.currentTarget.blur();
                                        }}
                                        className={classNames(style.navigationButton)}
                                        testId={testIds.navigation.reports.customViews.importReportViewButton}
                                    >
                                        {t("Common.uploadJSONView")}
                                    </Button>

                                    <NavigationDivider />
                                </Box>
                            )}
                        </React.Fragment>
                    );
                }

                return (
                    <NavigationSubMenuItem key={childroute.path} to={childroute.path} testId={childroute.testId}>
                        {t(childroute.title)}
                    </NavigationSubMenuItem>
                );
            })}

            <ImportReportViews
                modalVisible={isReportUploadModalVisible}
                setModalVisible={setIsReportUploadModalVisible}
            />
        </Box>
    );
};
